import {
  cloneElement,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'

/**
 * Primitivas de sobreposicao: Popover, Modal e Folha inferior.
 *
 * Sao as tres pecas que o layout novo precisa em todo o sistema — chip com
 * popover na barra superior, modal para escolha, e folha deslizante no celular.
 *
 * O que elas resolvem, e que nenhuma delas ganha "de graca":
 *
 *   foco preso        Tab dentro de um modal aberto nao pode sair para a pagina
 *                     atras dele. Sem isso, quem usa teclado "cai" num
 *                     conteudo que visualmente esta coberto.
 *   foco devolvido    ao fechar, o foco volta ao elemento que abriu. Sem isso,
 *                     o foco vai para o inicio da pagina e a pessoa se perde.
 *   Escape            fecha. Um overlay que so fecha pelo proprio X prende
 *                     quem navega por teclado.
 *   rolagem travada   com modal aberto, a pagina atras nao rola.
 *   posicionamento    o popover se reposiciona quando nao cabe na viewport —
 *                     no celular, um popover alinhado a direita sai da tela.
 *
 * Tudo em portal no <body>: dentro da arvore, um `overflow: hidden` de
 * qualquer ancestral cortaria a sobreposicao.
 */

// ═══════════════════════════════════════════════════ utilidades de foco

const SELETOR_FOCAVEL = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/**
 * Prende o Tab dentro do contêiner e devolve o foco ao fechar.
 *
 * Retornar o foco importa mais do que parece: sem isso, fechar um modal joga o
 * foco para o topo do documento, e quem navega por teclado precisa percorrer a
 * pagina inteira de novo para voltar onde estava.
 */
function usarFocoPreso(ativo: boolean, ref: React.RefObject<HTMLElement>) {
  const anterior = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!ativo) return
    anterior.current = document.activeElement as HTMLElement | null

    const caixa = ref.current
    if (caixa) {
      const primeiro = caixa.querySelector<HTMLElement>(SELETOR_FOCAVEL)
      // Se nao houver nada focavel, o proprio contêiner recebe o foco: assim o
      // leitor de tela anuncia o conteudo em vez de continuar na pagina.
      ;(primeiro ?? caixa).focus({ preventScroll: true })
    }

    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !ref.current) return
      const focaveis = [...ref.current.querySelectorAll<HTMLElement>(SELETOR_FOCAVEL)].filter(
        (el) => el.offsetParent !== null,
      )
      if (focaveis.length === 0) {
        e.preventDefault()
        return
      }
      const primeiro = focaveis[0]!
      const ultimo = focaveis[focaveis.length - 1]!
      if (e.shiftKey && document.activeElement === primeiro) {
        e.preventDefault()
        ultimo.focus()
      } else if (!e.shiftKey && document.activeElement === ultimo) {
        e.preventDefault()
        primeiro.focus()
      }
    }

    document.addEventListener('keydown', aoTeclar)
    return () => {
      document.removeEventListener('keydown', aoTeclar)
      anterior.current?.focus({ preventScroll: true })
    }
  }, [ativo, ref])
}

/** Trava a rolagem do corpo, compensando a largura da barra para nao "pular". */
function usarRolagemTravada(ativo: boolean) {
  useEffect(() => {
    if (!ativo) return
    const anterior = document.body.style.overflow
    const larguraBarra = window.innerWidth - document.documentElement.clientWidth
    document.body.style.overflow = 'hidden'
    // Sem compensar, a pagina "salta" para a direita ao abrir o modal.
    if (larguraBarra > 0) document.body.style.paddingRight = `${larguraBarra}px`
    return () => {
      document.body.style.overflow = anterior
      document.body.style.paddingRight = ''
    }
  }, [ativo])
}

function usarEscape(ativo: boolean, aoFechar: () => void) {
  useEffect(() => {
    if (!ativo) return
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        aoFechar()
      }
    }
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [ativo, aoFechar])
}

// ═════════════════════════════════════════════════════════════ popover

type Alinhamento = 'inicio' | 'centro' | 'fim'

interface PopoverProps {
  /** O gatilho. Recebe ref, aria-expanded e onClick automaticamente. */
  gatilho: ReactElement
  /**
   * Conteudo. Como funcao, recebe `fechar` — necessario para qualquer item que
   * age e some (um link de menu, um botao de "Sair"). Sem isso, cada uso teria
   * de manter o proprio `aberto` em paralelo ao do Popover, e os dois sairiam
   * de sincronia na primeira navegacao.
   */
  children: ReactNode | ((fechar: () => void) => ReactNode)
  /** Alinhamento horizontal em relacao ao gatilho. */
  alinhamento?: Alinhamento
  /** Rotulo acessivel do conteudo. */
  rotulo?: string
  larguraMinima?: string
}

/**
 * Popover ancorado ao gatilho — o chip "Ir agora ▾" da referencia.
 *
 * Posiciona por medicao real (getBoundingClientRect) e CORRIGE quando nao cabe
 * na viewport. Um popover alinhado a direita num celular de 360px sai da tela
 * se ninguem verificar, e o conteudo fica inalcancavel.
 */
export function Popover({
  gatilho,
  children,
  alinhamento = 'inicio',
  rotulo,
  larguraMinima = '15rem',
}: PopoverProps) {
  const [aberto, setAberto] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const refGatilho = useRef<HTMLElement>(null)
  const refCaixa = useRef<HTMLDivElement>(null)
  const id = useId()

  const fechar = useCallback(() => setAberto(false), [])
  usarEscape(aberto, fechar)
  usarFocoPreso(aberto, refCaixa)

  // useLayoutEffect: posiciona ANTES da pintura. Com useEffect, o popover
  // apareceria por um quadro no canto superior esquerdo antes de saltar para o
  // lugar — um piscar visível.
  useLayoutEffect(() => {
    if (!aberto || !refGatilho.current) return

    const posicionar = () => {
      const g = refGatilho.current?.getBoundingClientRect()
      const c = refCaixa.current?.getBoundingClientRect()
      if (!g) return

      const margem = 8
      const largura = c?.width ?? 240
      const altura = c?.height ?? 200

      let left =
        alinhamento === 'fim'
          ? g.right - largura
          : alinhamento === 'centro'
            ? g.left + g.width / 2 - largura / 2
            : g.left

      // Corrige quando escapa pela direita ou pela esquerda.
      left = Math.min(left, window.innerWidth - largura - margem)
      left = Math.max(margem, left)

      // Abre para cima quando não cabe embaixo — mas só se couber melhor lá.
      let top = g.bottom + margem
      if (top + altura > window.innerHeight - margem && g.top - altura - margem > margem) {
        top = g.top - altura - margem
      }

      setPos({ top, left })
    }

    posicionar()
    // Reposiciona em rolagem e redimensionamento: um popover ancorado que não
    // acompanha o gatilho fica flutuando solto na tela.
    window.addEventListener('resize', posicionar)
    window.addEventListener('scroll', posicionar, true)
    return () => {
      window.removeEventListener('resize', posicionar)
      window.removeEventListener('scroll', posicionar, true)
    }
  }, [aberto, alinhamento])

  useEffect(() => {
    if (!aberto) return
    const aoClicarFora = (e: MouseEvent) => {
      const alvo = e.target as Node
      if (refCaixa.current?.contains(alvo) || refGatilho.current?.contains(alvo)) return
      setAberto(false)
    }
    document.addEventListener('mousedown', aoClicarFora)
    return () => document.removeEventListener('mousedown', aoClicarFora)
  }, [aberto])

  const gatilhoComProps = cloneElement(gatilho, {
    ref: refGatilho,
    'aria-expanded': aberto,
    'aria-haspopup': 'dialog',
    'aria-controls': aberto ? id : undefined,
    onClick: (e: React.MouseEvent) => {
      // Preserva o handler proprio do gatilho, se houver, antes de alternar.
      const props = gatilho.props as {
        onClick?: (e: React.MouseEvent) => void
        aoClicar?: () => void
      }
      props.onClick?.(e)
      props.aoClicar?.()
      setAberto((v) => !v)
    },
  } as never)

  return (
    <>
      {gatilhoComProps}
      {aberto &&
        createPortal(
          <div
            ref={refCaixa}
            id={id}
            role="dialog"
            aria-label={rotulo}
            tabIndex={-1}
            className="ifr-popover"
            style={{
              // Posição calculada: o único inline legítimo aqui.
              top: pos?.top ?? 0,
              left: pos?.left ?? 0,
              minWidth: larguraMinima,
              // Invisível até medir, para não piscar no canto.
              visibility: pos ? 'visible' : 'hidden',
            }}
          >
            {typeof children === 'function' ? children(fechar) : children}
          </div>,
          document.body,
        )}
    </>
  )
}

// ═══════════════════════════════════════════════════════════════ modal

interface ModalProps {
  aberto: boolean
  aoFechar: () => void
  titulo: string
  children: ReactNode
  /** Rodape com as acoes. */
  acoes?: ReactNode
  tamanho?: 'pequeno' | 'medio' | 'grande'
  /** Impede fechar clicando fora — para escolha obrigatoria. */
  fecharSoNoBotao?: boolean
}

export function Modal({
  aberto,
  aoFechar,
  titulo,
  children,
  acoes,
  tamanho = 'medio',
  fecharSoNoBotao,
}: ModalProps) {
  const refCaixa = useRef<HTMLDivElement>(null)
  const idTitulo = useId()

  usarEscape(aberto && !fecharSoNoBotao, aoFechar)
  usarFocoPreso(aberto, refCaixa)
  usarRolagemTravada(aberto)

  if (!aberto) return null

  return createPortal(
    <div
      className="ifr-modal-fundo"
      onMouseDown={(e) => {
        // Fecha só quando o clique COMEÇA no fundo. Sem isso, arrastar uma
        // seleção de texto de dentro para fora fecharia o modal.
        if (!fecharSoNoBotao && e.target === e.currentTarget) aoFechar()
      }}
    >
      <div
        ref={refCaixa}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        tabIndex={-1}
        className={`ifr-modal ifr-modal--${tamanho}`}
      >
        <header className="ifr-modal__topo">
          <h2 id={idTitulo} className="ifr-titulo ifr-titulo--secao">
            {titulo}
          </h2>
          <button className="ifr-botao-icone" onClick={aoFechar} aria-label="Fechar">
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </header>

        <div className="ifr-modal__corpo">{children}</div>

        {acoes && <footer className="ifr-modal__rodape">{acoes}</footer>}
      </div>
    </div>,
    document.body,
  )
}

// ═════════════════════════════════════════════════════ folha inferior

interface FolhaProps {
  children: ReactNode
  /** Altura inicial: recolhida mostra só o topo; expandida ocupa mais. */
  estado?: 'recolhida' | 'meia' | 'expandida'
  aoMudarEstado?: (e: 'recolhida' | 'meia' | 'expandida') => void
  rotulo?: string
}

/**
 * Folha inferior deslizante — o padrao do celular na referencia.
 *
 * Tres alturas, com a alca arrastavel. NAO usa portal: a folha faz parte do
 * layout da tela do planejador, e num portal ela cobriria a barra de navegacao
 * inferior, que precisa continuar alcancavel.
 */
export function FolhaInferior({
  children,
  estado = 'meia',
  aoMudarEstado,
  rotulo = 'Painel de opções',
}: FolhaProps) {
  const [arrastando, setArrastando] = useState(false)
  const inicioY = useRef(0)

  const proximo = (dy: number): 'recolhida' | 'meia' | 'expandida' => {
    // Arrastar para cima (dy negativo) expande; para baixo, recolhe.
    const ordem = ['recolhida', 'meia', 'expandida'] as const
    const i = ordem.indexOf(estado)
    if (dy < -40) return ordem[Math.min(i + 1, 2)]!
    if (dy > 40) return ordem[Math.max(i - 1, 0)]!
    return estado
  }

  return (
    <section
      className={`ifr-folha ifr-folha--${estado} ${arrastando ? 'ifr-folha--arrastando' : ''}`}
      aria-label={rotulo}
    >
      {/* A alça é um botão de verdade: assim o teclado também alterna a altura,
          e não só o gesto de arraste. */}
      <button
        className="ifr-folha__alca"
        aria-label={`Painel ${estado}. Alternar altura.`}
        onClick={() =>
          aoMudarEstado?.(estado === 'expandida' ? 'meia' : estado === 'meia' ? 'expandida' : 'meia')
        }
        onTouchStart={(e) => {
          inicioY.current = e.touches[0]?.clientY ?? 0
          setArrastando(true)
        }}
        onTouchEnd={(e) => {
          setArrastando(false)
          const fim = e.changedTouches[0]?.clientY ?? inicioY.current
          const novo = proximo(fim - inicioY.current)
          if (novo !== estado) aoMudarEstado?.(novo)
        }}
      >
        <span className="ifr-folha__risco" aria-hidden="true" />
      </button>
      <div className="ifr-folha__corpo">{children}</div>
    </section>
  )
}

// ════════════════════════════════════════════════════════════════ chip

/**
 * Chip de filtro/seletor — o "Ir agora ▾" da referencia.
 *
 * forwardRef E repasse de props NAO sao detalhe: o Popover clona o gatilho
 * injetando `ref`, `onClick`, `aria-expanded`, `aria-haspopup` e
 * `aria-controls`. Um componente que aceita apenas props proprias descarta
 * todos eles em silencio — e o popover simplesmente nunca abre, sem erro
 * nenhum no console. Foi exatamente o defeito da primeira versao.
 *
 * Botao e nao div: precisa ser alcancavel por teclado e anunciado como
 * controle.
 */
export const Chip = forwardRef<
  HTMLButtonElement,
  {
    icone?: string
    children: ReactNode
    /** Atalho para onClick. `onClick` injetado pelo Popover tem precedencia. */
    aoClicar?: () => void
    ativo?: boolean
    comSeta?: boolean
  } & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'>
>(function Chip({ icone, children, aoClicar, ativo, comSeta = true, className, ...resto }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      className={`ifr-chip ${ativo ? 'ifr-chip--ativo' : ''} ${className ?? ''}`}
      onClick={aoClicar}
      // `...resto` DEPOIS do onClick: assim o onClick do Popover sobrescreve o
      // atalho aoClicar quando os dois existem.
      {...resto}
    >
      {icone && <i className={`fas ${icone}`} aria-hidden="true" />}
      <span>{children}</span>
      {comSeta && <i className="fas fa-chevron-down ifr-chip__seta" aria-hidden="true" />}
    </button>
  )
})

/** Controle segmentado — "Horário da partida | Horário de chegada". */
export function Segmentado<T extends string>({
  valor,
  aoMudar,
  opcoes,
  rotulo,
}: {
  valor: T
  aoMudar: (v: T) => void
  opcoes: ReadonlyArray<{ valor: T; rotulo: string; icone?: string }>
  rotulo: string
}) {
  return (
    <div className="ifr-segmentado" role="group" aria-label={rotulo}>
      {opcoes.map((o) => (
        <button
          key={o.valor}
          type="button"
          className={`ifr-segmentado__item ${valor === o.valor ? 'ifr-segmentado__item--ativo' : ''}`}
          aria-pressed={valor === o.valor}
          onClick={() => aoMudar(o.valor)}
        >
          {o.icone && <i className={`fas ${o.icone}`} aria-hidden="true" />}
          {o.rotulo}
        </button>
      ))}
    </div>
  )
}

// ═════════════════════════════════════════════════════ consulta de midia

/**
 * Acompanha uma media query em JS, casada com a do CSS.
 *
 * Existe por um motivo concreto: o planejador mostra o mesmo painel no cartao
 * flutuante (desktop) ou na folha inferior (celular). Renderizar OS DOIS e
 * esconder um com `display: none` parece inofensivo e nao e — duplica todo o
 * conteudo no DOM, e com ele os `id`. Com `#origem` duas vezes na pagina,
 * `<label for="origem">` aponta para a primeira ocorrencia, que no celular e
 * justamente a que esta escondida: o rotulo deixa de focar o campo, o leitor de
 * tela anuncia o campo errado e `getElementById` fica ambiguo. Montando apenas
 * o lado ativo, cada `id` volta a ser unico.
 *
 * `matchMedia` e nao `innerWidth`: dispara no evento em vez de exigir listener
 * de resize, e respeita zoom e mudanca de orientacao.
 */
export function usarMediaQuery(consulta: string): boolean {
  // Estado inicial ja correto: partir de `false` e corrigir no efeito faria a
  // versao de celular aparecer por um quadro no desktop.
  const [casa, setCasa] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(consulta).matches,
  )

  useEffect(() => {
    const mq = window.matchMedia(consulta)
    const aoMudar = (e: MediaQueryListEvent) => setCasa(e.matches)
    setCasa(mq.matches)
    mq.addEventListener('change', aoMudar)
    return () => mq.removeEventListener('change', aoMudar)
  }, [consulta])

  return casa
}
